/**
 * ApplicationsPage.js — list of the current user's application packs.
 *
 * RC 1.47.P0.2B Slice B (2026-05-03) — upgraded to consume the new
 * paginated envelope from `GET /api/application-packs?page=…` while
 * preserving bit-for-bit compatibility with the legacy plain-array
 * shape (Slice A backend keeps both modes alive during the 7-day
 * soak — see `routes/application_packs.list_application_packs`).
 *
 * URL-driven state:
 *   - `?q`         pack-name search (debounced 350ms into the URL).
 *   - `?status`    CSV multi-select chips (draft, ready, submitted,
 *                  archived).
 *   - `?sort`      one of newest | oldest | updated_desc |
 *                  status_asc | fit_desc | fit_asc.
 *   - `?page`      1-indexed page number.
 *   - `?page_size` items per page (default 25).
 *
 * Bulk archive/delete actions are unchanged. Selection state is
 * scoped to the visible page so "Select all visible" honestly
 * means "select what you can see right now".
 */

import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Package, Loader2, Archive, Trash2, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '@/lib/api';
import { cachedFetch, invalidate as cacheInvalidate, peek, subscribe as cacheSubscribe, KEYS } from '@/lib/listCache';
import PackListFilters from '@/components/PackListFilters';
import PackPaginationBar from '@/components/PackPaginationBar';
import ApplicationCard from '@/components/applications/ApplicationCard';

const PAGE_SIZE_DEFAULT = 25;

/**
 * Build a short human summary from a bulk action response so the toast
 * is honest about partial outcomes without spamming the user.
 */
function summarizeBulkArchive(res) {
  const parts = [];
  if (res.archived?.length) parts.push(`${res.archived.length} archived`);
  if (res.already_archived?.length) parts.push(`${res.already_archived.length} already archived`);
  if (res.invalid_transition?.length) {
    parts.push(
      `${res.invalid_transition.length} can't be archived directly (walk through submitted first)`,
    );
  }
  if (res.not_found?.length) parts.push(`${res.not_found.length} not found`);
  return parts.join(' · ') || 'No changes';
}

function summarizeBulkDelete(res) {
  const parts = [];
  if (res.deleted?.length) parts.push(`${res.deleted.length} deleted`);
  if (res.not_found?.length) parts.push(`${res.not_found.length} not found`);
  return parts.join(' · ') || 'No changes';
}

export default function ApplicationsPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  // ── URL → derived state ─────────────────────────────────────────
  const qFromUrl     = searchParams.get('q')         || '';
  const sortFromUrl  = searchParams.get('sort')      || 'newest';
  const statusStrUrl = searchParams.get('status')    || '';
  const statusesFromUrl = useMemo(
    () => statusStrUrl.split(',').map((s) => s.trim()).filter(Boolean),
    [statusStrUrl],
  );
  const page = Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1);
  const pageSize = Math.max(
    1, parseInt(searchParams.get('page_size') || String(PAGE_SIZE_DEFAULT), 10)
       || PAGE_SIZE_DEFAULT,
  );

  // ── Server data ─────────────────────────────────────────────────
  const [packs, setPacks] = useState([]);
  const [total, setTotal] = useState(0);
  const [unfilteredTotal, setUnfilteredTotal] = useState(null);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);
  const [jobsById, setJobsById] = useState({});
  const [variantsById, setVariantsById] = useState({});
  const [lettersById, setLettersById] = useState({});
  const [loading, setLoading] = useState(true);

  // RC 1.49.3 P1-3 — post-pack-creation feedback. The wizard
  // navigates to `/applications?highlight=<pack_id>` after a
  // successful create. We auto-scroll to the highlighted row and
  // pulse it for 2.5 s, then strip the query param so a refresh
  // doesn't re-trigger the animation.
  const highlightId = searchParams.get('highlight') || null;
  const [pulseId, setPulseId] = useState(null);
  const highlightHandledRef = useRef(false);

  // Multi-select state: id → true. Pruned in `load()` against the
  // currently-visible page so reordering / filtering / paging never
  // strands a stale selection.
  const [selectedIds, setSelectedIds] = useState({});
  const [bulkBusy, setBulkBusy] = useState(false);

  // RC 1.53 Applications-page redesign — only one card can be
  // expanded at a time. Opening a card auto-collapses the previous
  // one; clicking the same card again collapses it manually.
  const [expandedId, setExpandedId] = useState(null);
  const toggleExpanded = useCallback((id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  }, []);

  // ── URL writer ──────────────────────────────────────────────────
  const updateParams = useCallback((updates, { resetPage = false } = {}) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        Object.entries(updates).forEach(([k, v]) => {
          if (v === null || v === undefined || v === '') next.delete(k);
          else next.set(k, String(v));
        });
        if (resetPage) next.delete('page');
        return next;
      },
      { replace: false },
    );
  }, [setSearchParams]);

  const onQChange = useCallback(
    (v) => updateParams({ q: v || null }, { resetPage: true }),
    [updateParams],
  );
  const onStatusToggle = useCallback((value) => {
    const set = new Set(statusesFromUrl);
    if (set.has(value)) set.delete(value); else set.add(value);
    const csv = Array.from(set).join(',');
    updateParams({ status: csv || null }, { resetPage: true });
  }, [statusesFromUrl, updateParams]);
  const onSortChange = useCallback(
    (v) => updateParams({ sort: v === 'newest' ? null : v }, { resetPage: true }),
    [updateParams],
  );
  const onClearFilters = useCallback(() => {
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);
  const onPageChange = useCallback(
    (p) => updateParams({ page: p > 1 ? p : null }),
    [updateParams],
  );

  // ── Loader ──────────────────────────────────────────────────────
  // RC 1.49.4 Tier 1 — cache-aware loader.
  //
  //   • The "list envelope" cache is keyed by the full request
  //     query string so different sort/filter/page combinations
  //     don't collide.
  //   • `/api/cv-variants` and `/api/cover-letters` are cached
  //     under stable keys (no query string) — the wizard already
  //     reads + invalidates these.
  //   • Per-pack `/api/jobs/{id}` results are cached individually
  //     and shared across pages (Jobs page, Pack Detail, etc.) so
  //     navigating Apps → Pack Detail → back doesn't refetch the
  //     same job.
  //   • Stale-while-revalidate: if we have ANY cached envelope
  //     (even past TTL), the page renders it instantly with no
  //     skeleton. The background revalidation updates state via
  //     the subscribe() hook below.
  const _buildQs = useCallback(() => {
    const qs = new URLSearchParams();
    qs.set('page', String(page));
    qs.set('page_size', String(pageSize));
    if (sortFromUrl && sortFromUrl !== 'newest') qs.set('sort', sortFromUrl);
    if (statusesFromUrl.length > 0) qs.set('status', statusesFromUrl.join(','));
    if (qFromUrl) qs.set('q', qFromUrl);
    return qs.toString();
  }, [page, pageSize, sortFromUrl, statusesFromUrl, qFromUrl]);

  // Note: first-paint pre-hydration is performed in a one-shot
  // useEffect declared just above the load() effect below. Doing
  // it here as a render-time side-effect (the prior implementation)
  // tripped the StrictMode "setState on unmounted component"
  // warning during dev double-mount. The cache-shape bug it also
  // carried — `seed.data.envelope` was undefined because the cache
  // stores the envelope directly, not a `{ envelope, variants,
  // letters, jobs }` wrapper — silently blanked the page on every
  // cache-hit re-mount. Both are fixed in the effect below.

  function _applyEnvelope(envelope) {
    let packsList = [];
    if (Array.isArray(envelope)) {
      packsList = envelope;
      setTotal(envelope.length);
      setHasNext(false); setHasPrev(false); setUnfilteredTotal(null);
    } else if (envelope && Array.isArray(envelope.items)) {
      packsList = envelope.items;
      setTotal(Number(envelope.total) || 0);
      setHasNext(Boolean(envelope.has_next));
      setHasPrev(Boolean(envelope.has_prev));
      setUnfilteredTotal(
        typeof envelope.unfiltered_total === 'number' ? envelope.unfiltered_total : null,
      );
    } else {
      packsList = [];
      setTotal(0); setHasNext(false); setHasPrev(false); setUnfilteredTotal(null);
    }
    setPacks(packsList);
    setSelectedIds(prev => {
      const alive = new Set(packsList.map(p => p.id));
      const next = {};
      Object.keys(prev).forEach(id => { if (alive.has(id)) next[id] = true; });
      return next;
    });
    return packsList;
  }

  const load = useCallback(async () => {
    const qsString = _buildQs();
    const listKey = KEYS.applicationsList(qsString);
    const variantsKey = KEYS.cvVariantsAll();
    const lettersKey = KEYS.coverLettersAll();
    const seed = peek(listKey, 180_000);
    // Only show the spinner if we have NO data at all to render.
    if (!seed.hit) setLoading(true);

    try {
      const [envelope, vs, ls] = await Promise.all([
        cachedFetch(listKey, () => apiFetch(`/api/application-packs?${qsString}`).catch(() => null), { ttlMs: 180_000 }),
        cachedFetch(variantsKey, () => apiFetch('/api/cv-variants').catch(() => []), { ttlMs: 5 * 60_000 }),
        cachedFetch(lettersKey, () => apiFetch('/api/cover-letters').catch(() => []), { ttlMs: 5 * 60_000 }),
      ]);
      const packsList = _applyEnvelope(envelope);
      setVariantsById(Object.fromEntries((vs || []).map(v => [v.id, v])));
      setLettersById(Object.fromEntries((ls || []).map(l => [l.id, l])));

      // Per-pack job fan-out — each job cached individually under
      // jobs:by-id:{id}. Jobs almost never change after import, so
      // the TTL is generous (5 min). Mutations (status patch) on
      // the job invalidate the specific key.
      const uniqueJobIds = Array.from(new Set(packsList.map(p => p.job_id).filter(Boolean)));
      const jobResults = await Promise.all(
        uniqueJobIds.map(async (jid) => {
          try {
            const job = await cachedFetch(
              KEYS.jobById(jid),
              () => apiFetch(`/api/jobs/${jid}`),
              { ttlMs: 5 * 60_000 },
            );
            return [jid, job];
          } catch (e) {
            return [jid, e?.status === 404 ? { __removed: true } : null];
          }
        }),
      );
      const jobsByIdNext = Object.fromEntries(jobResults.filter(([, j]) => j !== null));
      setJobsById(jobsByIdNext);
    } finally {
      setLoading(false);
    }
  }, [_buildQs]);

  useEffect(() => { load(); }, [load]);

  // RC 1.53 (2026-05-12 PM) — Single page-level Gmail OAuth finalize
  // listener for ALL ApplicationCard instances. The OAuth popup posts
  // back to whichever window opened it; that window is *this* page
  // when a card kicks off `Send via Gmail` while Gmail is not yet
  // connected. We finalise once here and re-broadcast as a custom
  // `ndorflow-gmail-connected` event so every card can refresh its
  // local "connected" badge in lock-step. (One network call, N card
  // refreshes — same UX as Pack Detail's existing handler.)
  useEffect(() => {
    async function handler(ev) {
      if (!ev?.data || ev.data.source !== 'ndorflow-gmail') return;
      if (ev.data.status !== 'ok' || !ev.data.payload) return;
      try {
        await apiFetch('/api/gmail/oauth/finalize', {
          method: 'POST', body: ev.data.payload,
        });
        const email = ev.data.payload.email_address || null;
        toast.success(`Gmail connected${email ? ` as ${email}` : ''}.`);
        window.dispatchEvent(new CustomEvent('ndorflow-gmail-connected', {
          detail: { email },
        }));
      } catch (err) {
        toast.error(err?.message || 'Gmail finalize failed.');
      }
    }
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  // ── One-shot first-paint cache pre-hydration (StrictMode-safe) ──
  //
  // Replaces the prior render-time `_preHydrated.current` block.
  // Runs once after first commit so React never sees a setState
  // mid-render and the dev-double-mount cleanup path doesn't queue
  // a setState on an already-unmounted instance.
  //
  // Cache-shape fix: the list cache stores the API envelope DIRECTLY
  // (whatever `apiFetch('/api/application-packs?…')` returns — either
  // a plain array or `{ items, total, has_next, has_prev,
  // unfiltered_total }`). The pre-Slice-6c reader assumed a
  // `{ envelope, variants, letters, jobs }` wrapper that never
  // existed, so `seed.data.envelope` was always `undefined` and
  // `_applyEnvelope(undefined)` blanked `packs` on every cache-hit
  // mount. We now pass `seed.data` straight through — variants,
  // letters, and jobs each have their own cache keys that the
  // load() effect rehydrates synchronously on cache-hit.
  const _preHydrationDoneRef = useRef(false);
  useEffect(() => {
    if (_preHydrationDoneRef.current) return;
    _preHydrationDoneRef.current = true;
    const seedKey = KEYS.applicationsList(_buildQs());
    const seed = peek(seedKey, 180_000);
    if (!seed.hit || !seed.data) return;
    _applyEnvelope(seed.data);
    if (seed.fresh) setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // RC 1.49.4 Tier 1 — silent background revalidation hook. When a
  // mutation elsewhere invalidates the list cache (e.g. wizard's
  // createPack), the next mount of this page sees a cache miss and
  // refetches. When the user simply navigated away and back within
  // the TTL, the cache hit rendered instantly and no further work
  // happens. When the cache is stale, the load() above triggered
  // a revalidation; subscribe to its result so the rendered state
  // updates without a skeleton flash.
  useEffect(() => {
    const listKey = KEYS.applicationsList(_buildQs());
    const off = cacheSubscribe(listKey, (env) => {
      _applyEnvelope(env);
    });
    return off;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_buildQs]);

  // RC 1.24.W — Applications is a registry; inline edit handlers are
  // intentionally absent. All composition changes happen in Pack
  // Detail. The list rows render read-only "Selected CV" and
  // "Selected Cover Letter" labels.

  const rows = useMemo(() => (
    packs.map(p => ({
      pack: p,
      job: jobsById[p.job_id],
      variant: variantsById[p.cv_variant_id],
      letter: p.cover_letter_id ? lettersById[p.cover_letter_id] : null,
    }))
  ), [packs, jobsById, variantsById, lettersById]);

  // RC 1.49.3 P1-3 — scroll & pulse the freshly-created pack once
  // the rows have arrived. We require:
  //   • the URL still carries `?highlight=<id>`,
  //   • the targeted row is actually present on the current page
  //     (it should be; the wizard creates with default sort=newest),
  //   • we haven't already handled this highlight (one-shot per nav).
  // After firing, we strip `highlight` from the query so a refresh
  // doesn't replay the animation but other filters stay intact.
  useEffect(() => {
    if (!highlightId || loading || highlightHandledRef.current) return;
    const present = rows.some((r) => r.pack.id === highlightId);
    if (!present) return;
    highlightHandledRef.current = true;
    setPulseId(highlightId);
    // Defer scroll until React has flushed the row to the DOM.
    requestAnimationFrame(() => {
      const node = document.querySelector(
        `[data-testid="application-pack-row-${highlightId}"]`,
      );
      if (node && typeof node.scrollIntoView === 'function') {
        node.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
    const fadeTimer = setTimeout(() => setPulseId(null), 2500);
    const stripTimer = setTimeout(() => {
      // Strip `highlight` from the URL without triggering a reload
      // of the list. Preserve every other query param.
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('highlight');
          return next;
        },
        { replace: true },
      );
    }, 2700);
    return () => { clearTimeout(fadeTimer); clearTimeout(stripTimer); };
  }, [highlightId, loading, rows, setSearchParams]);

  const selectedCount = Object.keys(selectedIds).length;
  const selectedIdList = useMemo(() => Object.keys(selectedIds), [selectedIds]);
  const visibleIds = useMemo(() => rows.map(({ pack }) => pack.id), [rows]);
  const allVisibleSelected = visibleIds.length > 0
    && visibleIds.every((id) => selectedIds[id]);

  const toggleOne = useCallback((id, checked) => {
    setSelectedIds((prev) => {
      const next = { ...prev };
      if (checked) next[id] = true; else delete next[id];
      return next;
    });
  }, []);

  const toggleAllVisible = useCallback(() => {
    setSelectedIds((prev) => {
      // Always base the decision on the latest snapshot so the
      // "select all" checkbox stays honest even after rapid changes.
      const allSelectedNow = visibleIds.length > 0
        && visibleIds.every((id) => prev[id]);
      if (allSelectedNow) {
        // Clear only the visible ids; selections outside the current
        // list stay intact (there aren't any today but keeps it safe).
        const next = { ...prev };
        for (const id of visibleIds) delete next[id];
        return next;
      }
      const next = { ...prev };
      for (const id of visibleIds) next[id] = true;
      return next;
    });
  }, [visibleIds]);

  const clearSelection = useCallback(() => setSelectedIds({}), []);

  const bulkArchive = useCallback(async () => {
    if (bulkBusy || selectedIdList.length === 0) return;
    setBulkBusy(true);
    try {
      const res = await apiFetch('/api/application-packs/bulk-archive', {
        method: 'POST',
        body: { pack_ids: selectedIdList },
      });
      toast.success('Bulk archive', { description: summarizeBulkArchive(res) });
      clearSelection();
      // RC 1.49.4 — list changed, drop the cache before reloading.
      cacheInvalidate(['applications', 'application-packs:all']);
      await load();
    } catch (e) {
      toast.error('Bulk archive failed', { description: e.message });
    } finally {
      setBulkBusy(false);
    }
  }, [bulkBusy, selectedIdList, clearSelection, load]);

  const bulkDelete = useCallback(async () => {
    if (bulkBusy || selectedIdList.length === 0) return;
    // eslint-disable-next-line no-alert
    const confirmed = window.confirm(
      `Delete ${selectedIdList.length} pack${selectedIdList.length === 1 ? '' : 's'}? `
      + `Linked CVs, cover letters, and jobs stay intact.`,
    );
    if (!confirmed) return;
    setBulkBusy(true);
    try {
      const res = await apiFetch('/api/application-packs/bulk-delete', {
        method: 'POST',
        body: { pack_ids: selectedIdList },
      });
      toast.success('Bulk delete', { description: summarizeBulkDelete(res) });
      clearSelection();
      // RC 1.49.4 — list mutated; drop both the page-keyed list
      // cache AND the bare `application-packs:all` key used by
      // Jobs/Dashboard for badges/counts.
      cacheInvalidate(['applications', 'application-packs:all']);
      await load();
    } catch (e) {
      toast.error('Bulk delete failed', { description: e.message });
    } finally {
      setBulkBusy(false);
    }
  }, [bulkBusy, selectedIdList, clearSelection, load]);

  return (
    <div data-testid="applications-page" className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight text-ndor-ink font-outfit">Applications</h2>
        <p className="text-sm text-ndor-ink-secondary mt-1 font-manrope">
          Your application packs — each pinned to one job. A pack contains whatever you generated: your Source CV with Baseline Fit, or a tailored Variant CV with Variant Fit, plus an optional cover letter. Track status and export here.
        </p>
      </div>

      {/* RC 1.47.P0.2B — URL-driven filter bar (search + status
          chips + sort + clear filters + counter). Always rendered
          (even on empty state) so the user can see why their list
          is empty and lift filters without round-tripping. */}
      <PackListFilters
        qFromUrl={qFromUrl}
        statusesFromUrl={statusesFromUrl}
        sortFromUrl={sortFromUrl}
        total={total}
        unfilteredTotal={unfilteredTotal}
        onQChange={onQChange}
        onStatusToggle={onStatusToggle}
        onSortChange={onSortChange}
        onClearFilters={onClearFilters}
      />

      {selectedCount > 0 && (
        <div
          data-testid="bulk-action-bar"
          className="sticky top-0 z-10 -mx-2 px-4 py-2 rounded-md border border-ndor-edge bg-ndor-surface-alt shadow-sm flex items-center justify-between gap-3"
        >
          <div className="flex items-center gap-3">
            <label
              className="flex items-center gap-2 text-xs text-ndor-ink-muted cursor-pointer select-none"
              data-testid="bulk-select-all-label"
            >
              <Checkbox
                checked={allVisibleSelected}
                onCheckedChange={toggleAllVisible}
                aria-label={allVisibleSelected ? 'Clear all visible' : 'Select all visible'}
                data-testid="bulk-select-all-toggle"
              />
              {allVisibleSelected ? 'Clear all visible' : 'Select all visible'}
            </label>
            <span
              className="text-sm text-ndor-ink font-medium"
              data-testid="bulk-selected-count"
            >
              {selectedCount} selected
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={bulkArchive}
              disabled={bulkBusy}
              data-testid="bulk-archive-btn"
            >
              {bulkBusy ? (
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
              ) : (
                <Archive className="h-3 w-3 mr-1" />
              )}
              Archive
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={bulkDelete}
              disabled={bulkBusy}
              className="text-ndor-danger hover:text-ndor-danger"
              data-testid="bulk-delete-btn"
            >
              {bulkBusy ? (
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
              ) : (
                <Trash2 className="h-3 w-3 mr-1" />
              )}
              Delete
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={clearSelection}
              disabled={bulkBusy}
              data-testid="bulk-clear-btn"
            >
              <X className="h-3 w-3 mr-1" />
              Clear
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-ndor-ink-muted" />
        </div>
      ) : packs.length === 0 ? (
        <Card className="border-ndor-edge shadow-sm">
          <CardContent className="py-10 text-center space-y-2">
            <Package className="h-6 w-6 mx-auto text-ndor-ink-muted" />
            <p className="text-sm text-ndor-ink font-medium">
              {(qFromUrl || statusesFromUrl.length > 0)
                ? 'No packs match these filters'
                : 'No application packs yet'}
            </p>
            <p className="text-xs text-ndor-ink-muted max-w-sm mx-auto">
              {(qFromUrl || statusesFromUrl.length > 0)
                ? 'Try a different name or status, or clear filters to see all packs.'
                : <>Open a job, click <span className="font-medium text-ndor-ink">Start application pack</span>, and link a Source CV + CV variant to begin tracking.</>}
            </p>
            {(qFromUrl || statusesFromUrl.length > 0) ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={onClearFilters}
                className="text-ndor-info"
                data-testid="applications-empty-clear-filters"
              >
                Clear filters
              </Button>
            ) : (
              <>
                <Link to="/jobs" className="inline-block pt-3">
                  <span className="text-xs text-ndor-info hover:underline" data-testid="applications-empty-jobs-link">
                    Browse jobs →
                  </span>
                </Link>

                {/* RC 1.49.x Slice 8 — calm reviewer-guidance hint
                    mirroring Slice 7. Sequencing only — not a
                    walkthrough or product tour. Renders only on the
                    genuine empty state (no filters active), so
                    existing-user filtered-empty UX is untouched. */}
                <div
                  className="mt-5 mx-auto max-w-md rounded-md border border-ndor-edge bg-ndor-surface-alt/40 p-3 text-left"
                  data-testid="applications-empty-reviewer-hint"
                >
                  <p className="text-[10px] font-mono-ui uppercase tracking-[0.16em] text-ndor-ink-muted">
                    Recommended next step
                  </p>
                  <ol className="mt-2 space-y-1.5 text-xs text-ndor-ink-secondary leading-relaxed">
                    <li className="flex items-start gap-2">
                      <span className="font-medium text-ndor-ink">1.</span>
                      <span><Link to="/jobs" className="text-ndor-info hover:underline">Import or choose a job</Link> from the Jobs page.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-medium text-ndor-ink">2.</span>
                      <span>Run <span className="font-medium text-ndor-ink">Analyse Fit</span> on the job using your Source CV.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-medium text-ndor-ink">3.</span>
                      <span>Create an <span className="font-medium text-ndor-ink">application pack</span> from the job — it shows up here for tracking and export.</span>
                    </li>
                  </ol>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2.5" data-testid="applications-list">
          {rows.map(({ pack, job, variant, letter }) => (
            <ApplicationCard
              key={pack.id}
              pack={pack}
              job={job}
              variant={variant}
              letter={letter}
              selected={!!selectedIds[pack.id]}
              onToggleSelected={toggleOne}
              expanded={expandedId === pack.id}
              onToggleExpanded={toggleExpanded}
              isPulsing={pulseId === pack.id}
              onPackUpdated={(updated) => {
                // Patch the row in-place so the UI reflects the
                // newly-saved status/notes without a full reload.
                setPacks((prev) => prev.map((p) => (
                  p.id === pack.id ? { ...p, ...updated } : p
                )));
              }}
            />
          ))}
        </div>
      )}

      {/* RC 1.47.P0.2B — pagination bar. Renders only when the
          envelope advertises further pages (legacy array mode never
          triggers this — `hasNext`/`hasPrev` stay false). */}
      {!loading && packs.length > 0 && (
        <PackPaginationBar
          page={page}
          pageSize={pageSize}
          total={total}
          hasPrev={hasPrev}
          hasNext={hasNext}
          onPageChange={onPageChange}
        />
      )}
    </div>
  );
}
